'use client'

import { ScheduleTaskModal } from '@/app/scheduled-tasks/ScheduleTaskModal'
import Transition from '@/components/Motion/Transition'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { copilotEvents } from '@/lib/features/copilotv2/copilotEvents'
import { ProgressStep } from '@/lib/type'
import { cn, transformHeadlineForDisplay } from '@/lib/utils'
import { copilotToolMappings } from '@/utils/copilotToolMappings'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowRight,
  BarChart3,
  Bell,
  Brain,
  Bug,
  Calendar,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clock,
  Code,
  Copy,
  Cpu,
  Database,
  Download,
  Edit,
  Eye,
  FileText,
  Filter,
  Flag,
  GitBranch,
  Globe,
  Hash,
  Info,
  Key,
  Layers,
  Loader,
  Lock,
  Mail,
  MessageSquare,
  Minus,
  Move,
  Network,
  Package,
  Pause,
  PieChart,
  Play,
  Plus,
  Puzzle,
  RefreshCw,
  Save,
  Search,
  Server,
  Settings,
  Shield,
  Square,
  Target,
  Timer,
  Trash2,
  Unlock,
  Upload,
  Wrench,
  Zap,
} from 'lucide-react'
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { FaCheckCircle, FaCog, FaRegCircle, FaSpinner, FaTools } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { TemplateToolIdValues } from 'sprites-types/api/v2'
import { isToolSchedulable } from 'sprites-types/api/v2/scheduled-tasks'
import { ErrorDebugModal } from './ErrorDebugModal'

// Progress icon mapping for custom progress message icons
const progressIconMappings: Record<string, React.ReactNode> = {
  // Status icons
  checkmark: <CheckCircle2 size={14} className="text-green-500" />,
  success: <CheckCircle size={14} className="text-green-500" />,
  error: <RefreshCw size={14} className="text-blue-500" />,
  warning: <AlertCircle size={14} className="text-yellow-500" />,
  info: <Info size={14} className="text-blue-500" />,

  // Workflow and process icons
  workflow: <Settings size={14} className="text-blue-500" />,
  process: <RefreshCw size={14} className="text-blue-500" />,
  execution: <Play size={14} className="text-green-500" />,
  pause: <Pause size={14} className="text-yellow-500" />,
  stop: <Square size={14} className="text-red-500" />,

  // System and technical icons
  gear: <FaCog size={14} className="text-neutral-500" />,
  settings: <Settings size={14} className="text-neutral-500" />,
  timer: <Timer size={14} className="text-orange-500" />,
  clock: <Clock size={14} className="text-orange-500" />,
  code: <Code size={14} className="text-purple-500" />,
  hash: <Hash size={14} className="text-cyan-500" />,
  database: <Database size={14} className="text-blue-500" />,
  server: <Server size={14} className="text-gray-500" />,
  cpu: <Cpu size={14} className="text-purple-500" />,
  network: <Network size={14} className="text-blue-500" />,

  // File and data icons
  file: <FileText size={14} className="text-gray-500" />,
  document: <FileText size={14} className="text-gray-500" />,
  save: <Save size={14} className="text-green-500" />,
  upload: <Upload size={14} className="text-blue-500" />,
  download: <Download size={14} className="text-blue-500" />,

  // Workflow structure icons
  node: <Circle size={14} className="text-blue-500" />,
  edge: <ArrowRight size={14} className="text-gray-500" />,
  branch: <GitBranch size={14} className="text-green-500" />,
  layer: <Layers size={14} className="text-purple-500" />,
  flow: <ArrowDown size={14} className="text-blue-500" />,

  // Action icons
  add: <Plus size={14} className="text-green-500" />,
  remove: <Minus size={14} className="text-red-500" />,
  edit: <Edit size={14} className="text-blue-500" />,
  delete: <Trash2 size={14} className="text-red-500" />,
  copy: <Copy size={14} className="text-gray-500" />,
  move: <Move size={14} className="text-gray-500" />,

  // Validation and testing icons
  validate: <CheckCircle2 size={14} className="text-green-500" />,
  test: <Target size={14} className="text-purple-500" />,
  check: <CheckCircle size={14} className="text-green-500" />,
  verify: <Shield size={14} className="text-blue-500" />,

  // Summary and analytics icons
  summary: <Zap size={14} className="text-yellow-500" />,
  analytics: <BarChart3 size={14} className="text-blue-500" />,
  chart: <PieChart size={14} className="text-green-500" />,
  stats: <Activity size={14} className="text-purple-500" />,

  // Search and discovery icons
  search: <Search size={14} className="text-blue-500" />,
  find: <Search size={14} className="text-blue-500" />,
  discover: <Globe size={14} className="text-blue-500" />,

  // Security and access icons
  lock: <Lock size={14} className="text-red-500" />,
  unlock: <Unlock size={14} className="text-green-500" />,
  key: <Key size={14} className="text-yellow-500" />,
  secure: <Shield size={14} className="text-green-500" />,

  // Communication icons
  message: <MessageSquare size={14} className="text-blue-500" />,
  mail: <Mail size={14} className="text-blue-500" />,
  notification: <Bell size={14} className="text-yellow-500" />,

  // Time and scheduling icons
  schedule: <Calendar size={14} className="text-blue-500" />,
  deadline: <Clock size={14} className="text-red-500" />,
  duration: <Timer size={14} className="text-orange-500" />,

  // Loading and progress icons
  loading: <Loader size={14} className="text-blue-500" />,
  progress: <RefreshCw size={14} className="text-blue-500" />,
  spinning: <RefreshCw size={14} className="animate-spin text-blue-500" />,

  // Tool and utility icons
  tool: <Wrench size={14} className="text-gray-500" />,
  utility: <Settings size={14} className="text-gray-500" />,
  plugin: <Puzzle size={14} className="text-purple-500" />,

  // Thinking and AI icons
  thinking: <Brain size={14} className="text-purple-500" />,
  ai: <Cpu size={14} className="text-purple-500" />,
  model: <Layers size={14} className="text-purple-500" />,

  // Debug and development icons
  debug: <Bug size={14} className="text-red-500" />,
  log: <FileText size={14} className="text-gray-500" />,
  trace: <ArrowRight size={14} className="text-gray-500" />,

  // Build and deployment icons
  build: <Wrench size={14} className="text-orange-500" />,
  deploy: <Upload size={14} className="text-green-500" />,
  release: <Package size={14} className="text-blue-500" />,

  // Integration and API icons
  api: <Globe size={14} className="text-blue-500" />,
  integration: <Network size={14} className="text-green-500" />,
  webhook: <ArrowRight size={14} className="text-purple-500" />,

  // Data processing icons
  transform: <RefreshCw size={14} className="text-blue-500" />,
  filter: <Filter size={14} className="text-gray-500" />,
  aggregate: <BarChart3 size={14} className="text-green-500" />,

  // Error handling icons
  retry: <RefreshCw size={14} className="text-yellow-500" />,
  fallback: <ArrowDown size={14} className="text-orange-500" />,
  timeout: <Clock size={14} className="text-red-500" />,

  // Success and completion icons
  complete: <CheckCircle2 size={14} className="text-green-500" />,
  done: <CheckCircle size={14} className="text-green-500" />,
  finished: <Flag size={14} className="text-green-500" />,
}

type ProgressMessageItemProps = {
  messageId: string
  steps: ProgressStep[]
  chatListRef?: RefObject<HTMLDivElement | null>
  expanded?: boolean
  headline: string
  tool: string
  executionId?: string
  toolResultContent?: unknown
  toolCallInputs?: unknown
}

// Helper function to detect if an error message is verbose and should be replaced with debug info link
const isVerboseErrorMessage = (message: string): boolean => {
  if (!message) return false

  // Check for length threshold
  if (message.length >= 100) return true

  // Check for JSON-like content
  if (message.trim().startsWith('{') && message.trim().endsWith('}')) return true

  // Check for stack traces
  if (message.includes('Error:') && message.includes('\n')) return true

  // Check for technical patterns that suggest verbose errors
  const technicalPatterns = [
    'at Object.',
    'Failed to execute',
    'ValidationError:',
    'TypeError:',
    'ReferenceError:',
    'SyntaxError:',
    'Network Error',
    'Request failed',
    'status code',
    'stack trace',
  ]

  return technicalPatterns.some((pattern) => message.toLowerCase().includes(pattern.toLowerCase()))
}

// Helper function to create a clean debug info link text for verbose errors
const createDebugInfoLink = (toolName: string): string => {
  return `Tried ${toolName} - see debug information (i)`
}

// Helper function to get the display subtitle for a step
const getDisplaySubtitle = (
  step: ProgressStep,
  subtitleIndex: number,
  toolName?: string
): string => {
  if (!step.subtitles || step.subtitles.length === 0) {
    return step.label || step.id
  }

  let displayText: string

  // For completed steps (success/error), show the last subtitle (final state)
  if (step.status === 'success' || step.status === 'error') {
    displayText = step.subtitles[step.subtitles.length - 1] || step.label || step.id
  }
  // For in-progress steps, cycle through subtitles
  else if (step.status === 'in-progress') {
    displayText = step.subtitles[subtitleIndex % step.subtitles.length] || step.label || step.id
  }
  // For pending steps, show the first subtitle or fallback
  else {
    displayText = step.subtitles[0] || step.label || step.id
  }

  // For verbose error messages, check if we should replace with debug info link
  if (toolName && isVerboseErrorMessage(displayText)) {
    return createDebugInfoLink(toolName)
  }

  return displayText
}

// Thinking spinner component for in-progress states
function ThinkingSpinner() {
  return (
    <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
      <FaSpinner className="animate-spin text-neutral-500" size={14} />
    </div>
  )
}

// Animated subtitle component with fade transitions, horizontal scrolling, and streaming effects
const AnimatedSubtitle = ({
  step,
  subtitleIndex,
  className,
  toolName,
  onDebugInfoClick,
}: {
  step: ProgressStep
  subtitleIndex: number
  className?: string
  toolName?: string
  onDebugInfoClick?: (errorData: { toolName: string; errorMessage: string }) => void
}) => {
  const displayText = getDisplaySubtitle(step, subtitleIndex, toolName)
  const [isHovered, setIsHovered] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [streamingDots, setStreamingDots] = useState('')

  // Animate dots for streaming messages
  useEffect(() => {
    if (!step.isStreaming) {
      setStreamingDots('')
      return
    }

    const interval = setInterval(() => {
      setStreamingDots((prev) => {
        if (prev.length >= 3) return ''
        return prev + '.'
      })
    }, 500)

    return () => clearInterval(interval)
  }, [step.isStreaming])

  // Auto-scroll on hover for long text
  useEffect(() => {
    if (!isHovered || !scrollRef.current) return

    const element = scrollRef.current
    const scrollWidth = element.scrollWidth
    const clientWidth = element.clientWidth

    // Only scroll if content is actually longer than container
    if (scrollWidth <= clientWidth) return

    const scrollDuration = 3000 // 3 seconds to scroll from start to end
    const scrollDistance = scrollWidth - clientWidth
    const startTime = Date.now()

    const scroll = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / scrollDuration, 1)

      // Use ease-in-out for smooth scrolling
      const easeProgress =
        progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2

      element.scrollLeft = easeProgress * scrollDistance

      if (progress < 1 && isHovered) {
        requestAnimationFrame(scroll)
      }
    }

    const animationId = requestAnimationFrame(scroll)

    return () => {
      cancelAnimationFrame(animationId)
    }
  }, [isHovered])

  return (
    <div className={`overflow-hidden ${className || ''}`}>
      <AnimatePresence mode="wait">
        <motion.div
          ref={scrollRef}
          key={`${step.id}-${displayText}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          className="scrollbar-hide overflow-x-auto whitespace-pre-line break-words transition-all duration-300"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false)
            // Reset scroll position when mouse leaves
            if (scrollRef.current) {
              scrollRef.current.scrollLeft = 0
            }
          }}
          onClick={() => {
            // Check if this is a debug info link and handle click
            if (toolName && displayText.includes('see debug information (i)')) {
              const originalErrorMessage =
                step.subtitles?.[step.subtitles.length - 1] || step.label || step.id
              onDebugInfoClick?.({
                toolName,
                errorMessage: originalErrorMessage,
              })
            }
          }}
          style={{
            cursor: displayText.includes('see debug information (i)') ? 'pointer' : 'default',
          }}
        >
          <span className="inline-block">
            {displayText.includes('see debug information (i)') ? (
              <span className="text-blue-600 underline decoration-dotted">{displayText}</span>
            ) : (
              displayText
            )}
            {step.isStreaming && <span className="text-blue-500">{streamingDots}</span>}
          </span>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default function ProgressMessageItem({
  messageId,
  steps,
  expanded,
  headline,
  tool,
  executionId,
  toolResultContent,
  toolCallInputs,
}: ProgressMessageItemProps) {
  const { onOpenPanel, messages } = useCopilotContext()

  // Track if the user has manually toggled expansion
  const userToggled = useRef(false)
  const [isExpanded, setIsExpanded] = useState(expanded || false) // Use expanded prop if provided
  // Track current subtitle index for each step
  const [subtitleIndices, setSubtitleIndices] = useState<Record<string, number>>({})

  // Error debug modal state
  const [errorModalData, setErrorModalData] = useState<
    | {
        toolName: string
        errorMessage: string
        timestamp: string
        summary?: string
      }
    | undefined
  >(undefined)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)

  // Schedule task modal state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [toolCallData, setToolCallData] = useState<{
    function: {
      name: string
      arguments: Record<string, unknown>
    }
  } | null>(null)
  const [batchToolCalls, setBatchToolCalls] = useState<Array<{
    function: {
      name: string
      arguments: Record<string, unknown>
    }
  }> | null>(null)

  // Find the tool call data from assistant messages
  const findToolCallData = useCallback(() => {
    // Find assistant message with tool calls that matches this tool_call_id
    for (const msg of messages) {
      if (msg.role === 'assistant') {
        // Check universal_tool_calls first (new format)
        if ('universal_tool_calls' in msg && msg.universal_tool_calls) {
          const toolCall = msg.universal_tool_calls.find((tc) => tc.id === messageId)
          if (toolCall) {
            return {
              function: {
                name: toolCall.name,
                arguments: toolCall.arguments,
              },
            }
          }
        }
        // Check legacy tool_calls format
        if ('tool_calls' in msg && msg.tool_calls) {
          const toolCall = msg.tool_calls.find((tc) => tc.id === messageId)
          if (toolCall && 'function' in toolCall) {
            try {
              const args =
                typeof toolCall.function.arguments === 'string'
                  ? JSON.parse(toolCall.function.arguments)
                  : toolCall.function.arguments
              return {
                function: {
                  name: toolCall.function.name,
                  arguments: args,
                },
              }
            } catch (error) {
              console.warn('Failed to parse tool call arguments:', error)
            }
          }
        }
      }
    }
    console.warn('[ProgressMessageItem] Could not find tool call data', {
      messageId,
      tool,
      messagesCount: messages.length,
      assistantMessages: messages.filter((m) => m.role === 'assistant').length,
    })
    return null
  }, [messages, messageId, tool])

  const handleScheduleClick = useCallback(() => {
    const toolCall = findToolCallData()
    if (toolCall) {
      setToolCallData(toolCall)
      setIsScheduleModalOpen(true)
    } else {
      toast.error('Could not find tool call data. Please try again.')
    }
  }, [findToolCallData])

  // Find all successful tool calls from the same assistant message
  const findAllSuccessfulToolCallsFromSameMessage = useCallback(() => {
    const toolCallsList: Array<{
      function: { name: string; arguments: Record<string, unknown> }
    }> = []

    // Find the assistant message that contains this tool call
    for (const msg of messages) {
      if (msg.role === 'assistant') {
        let foundCurrent = false

        // Check universal_tool_calls format
        if ('universal_tool_calls' in msg && msg.universal_tool_calls) {
          const currentToolCall = msg.universal_tool_calls.find((tc) => tc.id === messageId)
          if (currentToolCall) {
            foundCurrent = true
            // Found the assistant message - now collect all successful tools
            for (const tc of msg.universal_tool_calls) {
              // Find corresponding tool result message
              const toolResultMsg = messages.find(
                (m) =>
                  m.role === 'tool' &&
                  'tool_call_id' in m &&
                  (m as any).tool_call_id === tc.id &&
                  (m as any).status === 'completed'
              )
              if (toolResultMsg && isToolSchedulable(tc.name)) {
                toolCallsList.push({
                  function: {
                    name: tc.name,
                    arguments: tc.arguments,
                  },
                })
              }
            }
          }
        }

        // Check legacy tool_calls format
        if (!foundCurrent && 'tool_calls' in msg && msg.tool_calls) {
          const currentToolCall = msg.tool_calls.find((tc) => tc.id === messageId)
          if (currentToolCall) {
            foundCurrent = true
            // Found the assistant message - now collect all successful tools
            for (const tc of msg.tool_calls) {
              if (!('function' in tc)) continue
              // Find corresponding tool result message
              const toolResultMsg = messages.find(
                (m) =>
                  m.role === 'tool' &&
                  'tool_call_id' in m &&
                  (m as any).tool_call_id === tc.id &&
                  (m as any).status === 'completed'
              )
              if (toolResultMsg && isToolSchedulable(tc.function.name)) {
                try {
                  const args =
                    typeof tc.function.arguments === 'string'
                      ? JSON.parse(tc.function.arguments)
                      : tc.function.arguments
                  toolCallsList.push({
                    function: {
                      name: tc.function.name,
                      arguments: args,
                    },
                  })
                } catch (error) {
                  console.warn('Failed to parse tool call arguments:', error)
                }
              }
            }
          }
        }

        if (foundCurrent) break
      }
    }

    return toolCallsList.length > 1 ? toolCallsList : null
  }, [messages, messageId])

  const handleScheduleAllClick = useCallback(() => {
    const toolCalls = findAllSuccessfulToolCallsFromSameMessage()
    if (toolCalls && toolCalls.length > 0) {
      setToolCallData(null)
      setBatchToolCalls(toolCalls)
      setIsScheduleModalOpen(true)
    } else {
      toast.error('Could not find multiple successful tool calls.')
    }
  }, [findAllSuccessfulToolCallsFromSameMessage])

  // Session-long execution history cache to preserve debug access
  // Keeps failed execution data available throughout the entire user session
  // Only cleared when component unmounts or new failed execution occurs
  const executionHistoryRef = useRef<{
    lastExecutionId: string | null
    lastFailedSteps: boolean
    lastTool: string | null
    preservedCanViewResults: boolean
    timestamp: number // For debouncing, not cache expiration
  }>({
    lastExecutionId: null,
    lastFailedSteps: false,
    lastTool: null,
    preservedCanViewResults: false,
    timestamp: 0,
  })

  const currentStepIndex = steps.findIndex(
    (step) => step.status === 'in-progress' || step.status === 'error'
  )
  const currentStep = currentStepIndex !== -1 ? steps[currentStepIndex] : steps[steps.length - 1]
  const toolInfo = copilotToolMappings[tool as keyof typeof copilotToolMappings]
  const isTemplateToolId = Object.values(TemplateToolIdValues).includes(tool as any)
  const hasCopilotToolResult = toolResultContent !== undefined
  // Enhanced canViewResults logic for better failed execution support
  const baseCanViewResults =
    tool === 'executeWorkflow' ||
    tool === 'rerunFromNodeExecution' ||
    isTemplateToolId ||
    hasCopilotToolResult

  // Debug logging for failed executions
  const hasFailedSteps = steps.some((step) => step.status === 'error')
  if (hasFailedSteps) {
    console.log('[ProgressMessageItem] Failed execution debug (session-long cache):', {
      tool,
      executionId,
      canViewResults: baseCanViewResults,
      isTemplateToolId,
      hasFailedSteps,
      stepsCount: steps.length,
      currentStepStatus: currentStep?.status,
      cachedExecutionId: executionHistoryRef.current.lastExecutionId,
      cachedFailedSteps: executionHistoryRef.current.lastFailedSteps,
    })
  }

  // Debounce rapid state changes to prevent race conditions
  const lastUpdateTimeRef = useRef<number>(0)
  const debounceIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Check if we should use cached execution data (session-long cache)
  const shouldUseCachedExecution = () => {
    const currentDataMissing = !executionId || !baseCanViewResults
    const hasCachedData =
      executionHistoryRef.current.lastExecutionId || executionHistoryRef.current.lastFailedSteps

    // Use cache whenever current data is missing but we have cached data
    // This provides session-long access to debug information
    return currentDataMissing && hasCachedData
  }

  // Debounced update to prevent rapid consecutive calls from causing issues
  const debouncedUpdateCache = useCallback(
    (newData: {
      executionId: string | undefined
      hasFailedSteps: boolean
      tool: string
      baseCanViewResults: boolean
    }) => {
      const now = Date.now()
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current

      // Prevent updates more frequent than 100ms to avoid race conditions
      if (timeSinceLastUpdate < 100) {
        if (debounceIntervalRef.current) {
          clearTimeout(debounceIntervalRef.current)
        }

        debounceIntervalRef.current = setTimeout(() => {
          debouncedUpdateCache(newData)
        }, 100)
        return
      }

      lastUpdateTimeRef.current = now
      const hasValidExecution = newData.executionId && newData.baseCanViewResults
      const hasSignificantFailure = newData.hasFailedSteps && newData.baseCanViewResults
      const currentCacheHasFailure = executionHistoryRef.current.lastFailedSteps

      // Update cache logic: preserve failed execution data throughout session
      if (hasValidExecution || hasSignificantFailure) {
        // For successful executions, only update if we don't have existing failed data
        // This preserves access to the last failed execution's debug info
        if (hasValidExecution && !hasSignificantFailure && currentCacheHasFailure) {
          console.log(
            '[ProgressMessageItem] Preserving existing failed execution data during successful execution'
          )
          // Don't overwrite cache that contains failed execution data
          return
        }

        executionHistoryRef.current = {
          lastExecutionId: newData.executionId || null,
          lastFailedSteps: newData.hasFailedSteps,
          lastTool: newData.tool,
          preservedCanViewResults: newData.baseCanViewResults,
          timestamp: now,
        }
      }
    },
    []
  )

  // Update session-long execution history cache (debounced)
  // Cache persists for the entire component lifecycle to provide continuous debug access
  useEffect(() => {
    debouncedUpdateCache({
      executionId,
      hasFailedSteps,
      tool,
      baseCanViewResults,
    })

    // Cleanup timeout on unmount
    return () => {
      if (debounceIntervalRef.current) {
        clearTimeout(debounceIntervalRef.current)
      }
    }
  }, [executionId, hasFailedSteps, tool, baseCanViewResults, debouncedUpdateCache])

  // Enhanced canViewResults with fallback to cached execution data
  const getStableCanViewResults = useCallback(() => {
    try {
      // Determine if workflow is still executing
      // Workflow is complete when no steps are in-progress or pending
      const hasActiveSteps = steps.some(
        (step) => step.status === 'in-progress' || step.status === 'pending'
      )

      // currentStepIndex === -1 means no step is in-progress or error,
      // which implies all steps are success (workflow completed successfully)
      // But we still need to check for pending steps explicitly
      const isWorkflowComplete = !hasActiveSteps

      // Primary logic - only show if workflow is complete
      if (baseCanViewResults && isWorkflowComplete) return true

      // Cache fallback - only if workflow is complete
      if (
        isWorkflowComplete &&
        shouldUseCachedExecution() &&
        executionHistoryRef.current.preservedCanViewResults
      ) {
        return true
      }

      return false
    } catch (error) {
      console.warn('[ProgressMessageItem] Error in getStableCanViewResults:', error)
      return false // Safe fallback - don't show button on error
    }
  }, [baseCanViewResults, hasFailedSteps, tool, isTemplateToolId, shouldUseCachedExecution, steps])

  const stableCanViewResults = getStableCanViewResults()

  // Use cached executionId if current one is missing but we have recent cache
  const getStableExecutionId = useCallback(() => {
    try {
      if (executionId) return executionId
      if (shouldUseCachedExecution() && executionHistoryRef.current.lastExecutionId) {
        return executionHistoryRef.current.lastExecutionId
      }
      return null
    } catch (error) {
      console.warn('[ProgressMessageItem] Error in getStableExecutionId:', error)
      return executionId // Safe fallback
    }
  }, [executionId, shouldUseCachedExecution])

  const stableExecutionId = getStableExecutionId()

  const handleViewExecutionResults = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (!onOpenPanel) {
        console.error('[ProgressMessageItem] onOpenPanel not available in context')
        return
      }

      // Copilot tool result - open with stored content directly
      if (hasCopilotToolResult) {
        onOpenPanel(
          {
            type: 'copilot-tool-result',
            toolName: tool,
            content: toolResultContent,
            inputs: toolCallInputs,
          },
          [{ name: 'Tool Result', index: 0 }]
        )
        return
      }

      const effectiveExecutionId = stableExecutionId || executionId

      if (!effectiveExecutionId) {
        // For failed executions, provide more helpful messaging
        if (hasFailedSteps || executionHistoryRef.current.lastFailedSteps) {
          toast.warn(
            'Execution data is not available for this failed workflow. Check console logs for debugging information.'
          )
          console.warn('[ProgressMessageItem] Failed execution missing executionId:', {
            tool,
            steps: steps.map((s) => ({ id: s.id, status: s.status, label: s.label })),
            headline,
            cachedData: executionHistoryRef.current,
            shouldUseCached: shouldUseCachedExecution(),
          })
        } else {
          toast.info('No execution data available for this message')
        }
        return
      }

      onOpenPanel(
        {
          type: 'workflow-execution-results',
          executionId: effectiveExecutionId,
        },
        [{ name: 'Workflow Results', index: 0 }]
      )
    },
    [
      stableExecutionId,
      executionId,
      onOpenPanel,
      hasFailedSteps,
      tool,
      steps,
      headline,
      shouldUseCachedExecution,
      hasCopilotToolResult,
      toolResultContent,
      toolCallInputs,
    ]
  )

  // Safe button rendering with error boundary
  const renderViewResultsButton = useCallback(() => {
    try {
      const shouldShowButton = stableCanViewResults

      if (!shouldShowButton) return null

      return (
        <button
          onClick={handleViewExecutionResults}
          aria-label="View execution results"
          className={cn(
            'flex items-center gap-1 text-xs font-medium transition-colors',
            'text-neutral-400 hover:text-neutral-600'
          )}
          title="View execution results"
        >
          <Eye className="h-3 w-3" />
          <span>View Results</span>
          <ChevronDown className="h-3 w-3 rotate-[-90deg]" />
        </button>
      )
    } catch (error) {
      console.warn('[ProgressMessageItem] Error rendering View Results button:', error)
      return null
    }
  }, [stableExecutionId, stableCanViewResults, hasFailedSteps, handleViewExecutionResults])

  // Render schedule all button - only show on the last successful tool in a batch
  const renderScheduleAllButton = useCallback(() => {
    const allToolCalls = findAllSuccessfulToolCallsFromSameMessage()
    if (!allToolCalls) return null

    // Only show on the last tool result - check if this is the last one
    const lastToolCall = allToolCalls[allToolCalls.length - 1]
    const currentToolCall = findToolCallData()
    if (
      !currentToolCall ||
      lastToolCall.function.name !== currentToolCall.function.name ||
      JSON.stringify(lastToolCall.function.arguments) !==
        JSON.stringify(currentToolCall.function.arguments)
    ) {
      return null
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          handleScheduleAllClick()
        }}
        aria-label={`Schedule all ${allToolCalls.length} successful tools`}
        className={cn(
          'flex items-center gap-1 text-xs font-medium transition-colors',
          'text-blue-500 hover:text-blue-700'
        )}
        title={`Schedule all ${allToolCalls.length} successful tools from this interaction`}
      >
        <Calendar className="h-3 w-3" />
        <span>Schedule all ({allToolCalls.length})</span>
      </button>
    )
  }, [findAllSuccessfulToolCallsFromSameMessage, findToolCallData, handleScheduleAllClick])

  // Handle clicking on a scheduleTask tool to reopen the modal
  const handleScheduleTaskClick = useCallback(() => {
    if (tool !== 'scheduleTask') return
    // Get the prefill data directly from the tool call arguments
    const toolCall = findToolCallData()
    if (!toolCall) {
      toast.error('Could not find schedule task data. Please try again.')
      return
    }
    // The scheduleTask tool arguments ARE the prefill data
    const args = toolCall.function.arguments as {
      name: string
      description?: string | null
      toolCalls: Array<{ function: { name: string; arguments: Record<string, unknown> } }>
      repeatSpec?: {
        kind: 'daily' | 'every_x_days' | 'weekly' | 'monthly'
        startHour?: number | null
        interval?: number | null
        daysOfWeek?: number[] | null
        monthlyOn?:
          | {
              type: 'dayOfMonth'
              day: number
            }
          | {
              type: 'nthWeekday'
              monthWeekday: { which: number; weekday: number }
            }
          | null
      } | null
      timezone?: string | null
      maxRuns?: number | null
    }

    // Normalize repeatSpec to convert null values to undefined for type compatibility
    const normalizedRepeatSpec = args.repeatSpec
      ? {
          kind: args.repeatSpec.kind,
          startHour: args.repeatSpec.startHour ?? undefined,
          interval: args.repeatSpec.interval ?? undefined,
          daysOfWeek: args.repeatSpec.daysOfWeek ?? undefined,
          monthlyOn: args.repeatSpec.monthlyOn ?? undefined,
        }
      : undefined

    copilotEvents.emitOpenScheduleModal(messageId, {
      name: args.name,
      description: args.description ?? undefined,
      toolCalls: args.toolCalls,
      repeatSpec: normalizedRepeatSpec,
      timezone: args.timezone ?? undefined,
      maxRuns: args.maxRuns ?? undefined,
    })
  }, [tool, messageId, findToolCallData])

  // Render schedule button - show for completed tool calls (except blacklisted chat-only tools)
  const renderScheduleButton = useCallback(() => {
    if (!isToolSchedulable(tool)) return null
    // Show schedule button for all completed tool calls:
    // - Copilot tools (Layer 1 tools like agentWebSearch, etc.)
    // - Workflow executions (executeWorkflow, rerunFromNodeExecution, template tools)
    const isCompleted = currentStep.status === 'success'
    if (!isCompleted) return null

    const toolCall = findToolCallData()
    if (!toolCall) {
      // Don't show button if we can't find the tool call data
      // This can happen if the assistant message's tool_calls weren't preserved
      return null
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          handleScheduleClick()
        }}
        aria-label="Schedule this task"
        className={cn(
          'flex items-center gap-1 text-xs font-medium transition-colors',
          'text-neutral-400 hover:text-neutral-600'
        )}
        title="Schedule this task to run automatically"
      >
        <Clock className="h-3 w-3" />
        <span>Schedule</span>
      </button>
    )
  }, [tool, currentStep.status, findToolCallData, handleScheduleClick])

  const toggleExpanded = useCallback(() => {
    setIsExpanded((v) => !v)
    userToggled.current = true
    console.log('toggleExpanded', steps)
  }, [])

  // Handle debug info click
  const handleDebugInfoClick = useCallback(
    (errorData: { toolName: string; errorMessage: string }) => {
      const toolMapping = copilotToolMappings[tool as keyof typeof copilotToolMappings]
      const displayName = toolMapping?.displayName || tool

      setErrorModalData({
        toolName: displayName,
        errorMessage: errorData.errorMessage,
        timestamp: new Date().toLocaleString(),
        summary: `${displayName} execution failed`,
      })
      setIsErrorModalOpen(true)
    },
    [tool]
  )

  // Handle modal close
  const handleErrorModalClose = useCallback(() => {
    setIsErrorModalOpen(false)
    setErrorModalData(undefined)
  }, [])

  // Cycle through subtitles for in-progress steps
  useEffect(() => {
    const interval = setInterval(() => {
      setSubtitleIndices((prev) => {
        const newIndices = { ...prev }
        let hasChanges = false

        steps.forEach((step) => {
          if (step.status === 'in-progress' && step.subtitles && step.subtitles.length > 1) {
            const currentIndex = newIndices[step.id] || 0
            newIndices[step.id] = (currentIndex + 1) % step.subtitles.length
            hasChanges = true
          }
        })

        return hasChanges ? newIndices : prev
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [steps])

  // Auto-collapse when all steps are finished (success or error), unless user toggled
  useEffect(() => {
    const allFinished = steps.every((step) => step.status === 'success' || step.status === 'error')
    if (allFinished && isExpanded && !userToggled.current) {
      setIsExpanded(false)
    }
  }, [steps, isExpanded])

  const [isHovered, setIsHovered] = useState(false)

  // Helper to get status icon and color
  const getStatusIcon = (status: ProgressStep['status'], isStreaming?: boolean) => {
    if (isStreaming) return <FaSpinner className="animate-pulse text-blue-500" size={14} />
    if (status === 'in-progress')
      return <FaSpinner className="animate-spin text-neutral-500" size={14} />
    if (status === 'success') return <FaCheckCircle className="text-green-500" size={14} />
    if (status === 'error') return <RefreshCw className="text-blue-500" size={14} />
    return <FaRegCircle className="text-neutral-300" size={14} />
  }

  // Helper to get step-specific icon or fallback to status icon
  const getStepIcon = (step: ProgressStep, useStatusIcon = false) => {
    if (useStatusIcon || !step.icon) {
      return getStatusIcon(step.status, step.isStreaming)
    }
    return progressIconMappings[step.icon] || getStatusIcon(step.status, step.isStreaming)
  }
  /**
   * Renders the tool icon with consistent 14px sizing.
   * Expects icons from copilotToolMappings to be React Icons that accept a size prop.
   * Falls back to FaTools if no icon is available or cloning fails.
   */
  const renderToolIcon = () => {
    if (toolInfo && toolInfo.icon) {
      // Validate that we have a valid React element before attempting to clone
      if (!React.isValidElement(toolInfo.icon)) {
        console.warn(`Invalid React element for tool "${tool}":`, toolInfo.icon)
        return <FaTools size={14} />
      }

      try {
        // Clone the icon with size=14, assuming it's a React Icon component
        return React.cloneElement(toolInfo.icon, { size: 14 } as any)
      } catch (error) {
        console.warn(`Failed to clone icon for tool "${tool}":`, error)
        return <FaTools size={14} />
      }
    }
    return <FaTools size={14} />
  }
  const getStatusText = (status: ProgressStep['status']) => {
    if (status === 'in-progress') return 'In Progress'
    if (status === 'success') return 'Success'
    if (status === 'error') return 'Error'
    return 'Pending'
  }

  // Map ProgressStep status to transformation function expected status
  const mapStatusForTransformation = (
    status: ProgressStep['status']
  ): 'started' | 'completed' | 'failed' => {
    if (status === 'error') return 'failed'
    if (status === 'success') return 'completed'
    return 'started' // for 'in-progress' and 'pending'
  }

  // Transform headline for display
  const displayHeadline = transformHeadlineForDisplay(
    headline,
    mapStatusForTransformation(currentStep.status)
  )

  const isInProgress = currentStep.status === 'in-progress' || currentStep.status === 'pending'
  const showThinkingAnimation = isInProgress && !currentStep.isStreaming

  // Check if this is a clickable scheduleTask tool
  const isClickableScheduleTask = tool === 'scheduleTask' && currentStep.status === 'success'

  // Special button-like rendering for completed scheduleTask
  if (isClickableScheduleTask) {
    return (
      <button
        onClick={handleScheduleTaskClick}
        className={cn(
          'group relative my-1 flex items-center gap-3 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-left transition-all hover:border-blue-300 hover:bg-blue-100',
          'cursor-pointer'
        )}
      >
        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-white">
          <Calendar className="h-3 w-3" />
        </span>
        <span className="text-sm font-medium text-blue-700">Review & Schedule Task</span>
        <ChevronDown className="ml-auto h-4 w-4 rotate-[-90deg] text-blue-400" />
      </button>
    )
  }

  return (
    <div
      className={cn(
        'group relative my-1 border border-gray-200/50 bg-white/70 px-4 py-2 backdrop-blur-[8px] transition-[background-color,border-color] hover:bg-white/90',
        isExpanded && steps.length > 1 ? 'rounded-2xl' : 'rounded-full'
      )}
      style={{
        borderRadius: isExpanded && steps.length > 1 ? '1rem' : '9999px',
        transition: 'background-color 0.2s, border-color 0.2s',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Transition>
        <div className="relative w-full">
          {(!isExpanded || steps.length <= 1) && (
            <>
              <div className="flex items-center gap-2">
                {showThinkingAnimation ? (
                  <ThinkingSpinner />
                ) : (
                  <span
                    title={getStatusText(currentStep.status)}
                    className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center"
                  >
                    {isHovered
                      ? getStatusIcon(currentStep.status, currentStep.isStreaming)
                      : currentStep.icon
                        ? getStepIcon(currentStep, false)
                        : renderToolIcon()}
                  </span>
                )}
                {steps.length > 1 ? (
                  <button
                    onClick={toggleExpanded}
                    aria-label="Expand progress report"
                    className="flex items-center gap-1 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-600"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <span>{displayHeadline}</span>
                    <ChevronDown className="h-4 w-4 flex-shrink-0" />
                  </button>
                ) : (
                  <span className="text-sm font-medium text-neutral-400">{displayHeadline}</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {renderScheduleAllButton()}
                  {renderScheduleButton()}
                  {stableCanViewResults && renderViewResultsButton()}
                </div>
              </div>
              {/* Show subtitle/details for in-progress steps */}
              {isInProgress && currentStep.subtitles && currentStep.subtitles.length > 0 && (
                <div className="ml-6 mt-1">
                  <AnimatedSubtitle
                    step={currentStep}
                    subtitleIndex={subtitleIndices[currentStep.id] || 0}
                    className="min-w-0 flex-1 text-xs text-neutral-400"
                    toolName={tool}
                    onDebugInfoClick={handleDebugInfoClick}
                  />
                </div>
              )}
            </>
          )}
          {isExpanded && steps.length > 1 && (
            <>
              <div className="mb-2 flex items-center gap-2 py-0.5">
                {showThinkingAnimation ? (
                  <ThinkingSpinner />
                ) : (
                  <span
                    title={getStatusText(currentStep.status)}
                    className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center"
                  >
                    {isHovered
                      ? getStatusIcon(currentStep.status, currentStep.isStreaming)
                      : renderToolIcon()}
                  </span>
                )}
                {steps.length > 1 ? (
                  <button
                    onClick={toggleExpanded}
                    aria-label="Expand progress report"
                    className="flex cursor-pointer select-none items-center gap-1 text-sm font-medium text-neutral-400 transition-colors hover:text-neutral-600"
                    style={{ background: 'none', border: 'none', padding: 0 }}
                  >
                    <span>{displayHeadline}</span>
                    <motion.div
                      initial={false}
                      animate={{ rotate: 180 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="flex-shrink-0"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.div>
                  </button>
                ) : (
                  <span className="text-sm font-medium text-neutral-400">{displayHeadline}</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {renderScheduleAllButton()}
                  {renderScheduleButton()}
                  {stableCanViewResults && renderViewResultsButton()}
                </div>
              </div>
              <div className="ml-6 flex flex-col gap-1.5">
                {steps.map((step, idx) =>
                  // Skip the current step in the list if it's already shown at the top
                  idx === currentStepIndex ? null : (
                    <div
                      key={`${step.id}-${idx}`}
                      className="flex items-start gap-2 rounded px-1 py-0.5"
                    >
                      <span className="mt-0.5 flex h-3 w-3 flex-shrink-0 items-center justify-center">
                        {getStepIcon(step, false)}
                      </span>
                      <AnimatedSubtitle
                        step={step}
                        subtitleIndex={subtitleIndices[step.id] || 0}
                        className={`min-w-0 flex-1 text-xs ${
                          step.status === 'success' ? 'text-neutral-500' : 'text-neutral-400'
                        }`}
                        toolName={tool}
                        onDebugInfoClick={handleDebugInfoClick}
                      />
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </Transition>

      {/* Error Debug Modal */}
      <ErrorDebugModal
        isOpen={isErrorModalOpen}
        onClose={handleErrorModalClose}
        errorData={errorModalData}
      />

      {/* Schedule Task Modal */}
      {(toolCallData || batchToolCalls) && (
        <ScheduleTaskModal
          isOpen={isScheduleModalOpen}
          onClose={() => {
            setIsScheduleModalOpen(false)
            setToolCallData(null)
            setBatchToolCalls(null)
          }}
          toolCall={toolCallData ?? undefined}
          toolCalls={batchToolCalls ?? undefined}
          toolName={batchToolCalls ? `${batchToolCalls.length} tools` : tool}
        />
      )}
    </div>
  )
}
